import React, {ReactElement, useState} from 'react';
import {DragDropContext, Draggable, Droppable} from 'react-beautiful-dnd';
import {useTranslation} from 'react-i18next';
import {Icon} from '@iconify/react';
import {STATIC_TABLE_DEFAULT_CELL_VALUE} from '../../../../../constants';
import {IElementStaticTable} from '../../../../../incase-domain/elements/staticTable';
import Modal from '../../../../../incase-domain/components/modals/directory/Modal';
import {swapArrayIndices} from '../../../../../utils/utils';
import ElementShadow from '../../common/elementShadow';
import FontAndBackgroundColorPicker, {COLOR_FIELDS} from '../../common/fontAndBackgroundColorPicker';
import FontSelect from '../../common/fontSelect';
import SegmentDialog from './segment.dialog';
import TableSizeInput from './tableSizeInput';
import {TABS} from '../../../form/part/tabs';
import AnimationForm from '../../common/animationForm';
import DebouncedNumberInput from '../../common/debounced/DebounceNumberInput';
import DebouncedTextInput from '../../common/debounced/DebouncedTextInput';
import {LIST_ITEM_MAX} from '../../../../../incase-domain/validators/elements/constant';
import Button from '../../../../../incase-domain/components/button/Button';
import Label from '../../../../../incase-domain/components/label/Label';
import {useHighlightedElement, useSetHighlightedElement} from '../../../../../utils/hooks/slideUpdates/useSetSlideElement';

interface HighlightedStaticTableElementComponentProps {
    activeTab: string;
}

/*
 * @deprecated
 * */
function HighlightedStaticTableElementComponent(props: HighlightedStaticTableElementComponentProps): ReactElement {
    const {activeTab} = props;
    const highlightedElement = useHighlightedElement() as IElementStaticTable;
    const {font} = highlightedElement;
    const {t} = useTranslation();
    const updateHighlightedElement = useSetHighlightedElement();

    const [rowStyleDialog, setRowStyleDialog] = useState({
        show: false,
        index: -1,
    });

    const [columnStyleDialog, setColumnStyleDialog] = useState({
        show: false,
        index: -1,
    });

    function swapItemOrderHandler(newIndex: number, oldIndex: number): void {
        const newColumns = swapArrayIndices(highlightedElement.columns || [], newIndex, oldIndex);

        const oldStyle = {...highlightedElement.rowStyles[oldIndex]};
        const newStyle = {...highlightedElement.rowStyles[newIndex]};

        const newStyles = {...highlightedElement.rowStyles};
        newStyles[newIndex] = oldStyle;
        newStyles[oldIndex] = newStyle;

        updateHighlightedElement.multiple(['columns', 'rowStyles'], [newColumns, newStyles]);
    }

    function getMaxRowLength(): number {
        return Math.max(...[...highlightedElement.columns].map((column) => column.length));
    }

    function addColumn(): void {
        const newRows = [...highlightedElement.columns].map((row) => [...row, STATIC_TABLE_DEFAULT_CELL_VALUE]);
        updateHighlightedElement.single('columns', newRows);
    }

    function removeLastColumn(): void {
        const newRows = [...highlightedElement.columns].map((row) => {
            const rowLength = row.length;
            return row.filter((cell, cellIndex) => cellIndex !== rowLength - 1);
        });
        updateHighlightedElement.single('columns', newRows);
    }

    function addRow(): void {
        const maxRowLength = getMaxRowLength();
        const newRow = Array(maxRowLength).fill(STATIC_TABLE_DEFAULT_CELL_VALUE);
        const newRows = [...highlightedElement.columns, newRow];
        updateHighlightedElement.single('columns', newRows);
    }

    function removeLastRow(): void {
        const rowLength = highlightedElement.columns.length - 1;
        const newRows = [...highlightedElement.columns].filter((row, index) => index !== rowLength);
        updateHighlightedElement.single('columns', newRows);
    }

    function rowChangeHandler(columnIndex: number, rowIndex: number, value: string): void {
        const newColumns = [...highlightedElement.columns].map((column, index) =>
            index === columnIndex ? column.map((cell, i) => (i === rowIndex ? value : cell)) : column
        );
        updateHighlightedElement.single('columns', newColumns);
    }

    function createTable(columnNum: number, rowNum: number, columns: string[][]): void {
        const newColumns: string[][] = [];

        function createRow(rowNum: number): string[] {
            const row = [];
            for (let i = 0; i <= rowNum; i++) {
                row.push('');
            }
            return row;
        }

        for (let i = 0; i <= columnNum; i++) {
            newColumns.push(createRow(rowNum));
        }

        for (let i = 0; i <= columnNum; i++) {
            for (let j = 0; j <= rowNum; j++) {
                if (columns.length > i && columns[i].length > j) {
                    newColumns[i][j] = columns[i][j];
                } else {
                    newColumns[i][j] = STATIC_TABLE_DEFAULT_CELL_VALUE;
                }
            }
        }

        updateHighlightedElement.single('columns', newColumns);
    }

    if (activeTab === TABS.STYLE.id) {
        return (
            <>
                <div className="l-group--form">
                    <div className="u-flex u-flex-col u-gap-2">
                        <div className="l-group__item">
                            <FontSelect
                                highlightedElement={highlightedElement}
                                highlightedElementChangeHandler={updateHighlightedElement}
                            />
                        </div>
                        <div className="u-flex u-h-between u-v-center u-gap-2">
                            <Label>{t('designSidepanel.fontSize')}</Label>
                            <DebouncedNumberInput
                                step={0.1}
                                min={0.1}
                                onChange={(newValue) =>
                                    updateHighlightedElement.single('font', {
                                        ...font,
                                        size: newValue,
                                    })
                                }
                                value={font.size}
                            />
                        </div>

                        <div className="u-flex u-h-between u-v-center u-gap-2">
                            <Label>{t('designSidepanel.borderSize')}</Label>
                            <DebouncedNumberInput
                                step={0.1}
                                min={0.1}
                                onChange={(newValue) => updateHighlightedElement.single('borderSize', newValue)}
                                value={highlightedElement.borderSize || 0.1}
                            />
                        </div>
                    </div>
                    <div className="u-mt-2">
                        <FontAndBackgroundColorPicker
                            highlightedElement={highlightedElement}
                            highlightedElementChangeHandler={updateHighlightedElement}
                            colorFields={[COLOR_FIELDS.BACKGROUND_COLOR, COLOR_FIELDS.BORDER_COLOR, COLOR_FIELDS.FONT_COLOR]}
                        />
                    </div>
                </div>
                <ElementShadow highlightedElement={highlightedElement} highlightedElementChangeHandler={updateHighlightedElement} />
            </>
        );
    }

    if (activeTab === TABS.ADVANCED.id) {
        return (
            <>
                <AnimationForm
                    appearAfter={highlightedElement.appearAfter || 0}
                    elementAnimations={highlightedElement.elementAnimations}
                    highlightedElementChangeHandler={updateHighlightedElement}
                />
            </>
        );
    }

    return (
        <>
            <div className="l-group--form">
                <div className="l-group__item u-flex u-flex-col u-gap-1">
                    <div className="u-flex u-h-between u-v-center">
                        <Label>{t('designSidepanel.row')}</Label>
                        <div className="u-flex">
                            <Button variant="ghost" leading="ph:plus" size="xs" square onClick={addRow} />
                            <Button variant="ghost" leading="ph:minus" size="xs" square onClick={removeLastRow} />
                        </div>
                    </div>

                    <div className="u-flex u-h-between u-v-center">
                        <Label>{t('designSidepanel.column')}</Label>
                        <div className="u-flex">
                            <Button variant="ghost" leading="ph:plus" size="xs" square onClick={addColumn} />
                            <Button variant="ghost" leading="ph:minus" size="xs" square onClick={removeLastColumn} />
                        </div>
                    </div>
                </div>

                <div className="l-group__item u-flex u-v-center u-h-center">
                    <TableSizeInput
                        createTable={(colNum, rowNum) => {
                            createTable(colNum, rowNum, highlightedElement.columns);
                        }}
                        maxColumnSize={4}
                        maxRowSize={4}
                    />
                </div>
            </div>

            <div className="l-group--form">
                <div className="l-group__item">
                    <DragDropContext
                        onDragEnd={(e) => {
                            if (typeof e.destination?.index !== 'undefined') swapItemOrderHandler(e.destination.index, e.source.index);
                        }}
                    >
                        <Droppable droppableId={highlightedElement.id}>
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {highlightedElement.columns.map((column, columnIndex) => {
                                        return (
                                            <Draggable key={`${columnIndex}`} draggableId={`${columnIndex}`} index={columnIndex}>
                                                {(provided) => {
                                                    return (
                                                        <div
                                                            ref={provided.innerRef}
                                                            {...provided.draggableProps}
                                                            {...provided.dragHandleProps}
                                                            key={`highlighted-element-column-${columnIndex}`}
                                                        >
                                                            <div className="u-flex u-flex-col">
                                                                <div className="u-flex">
                                                                    {column.map((row, rowIndex) => {
                                                                        function renderColumnStyleIcon(
                                                                            columns: string[][],
                                                                            columnIndex: number
                                                                        ): JSX.Element {
                                                                            if (columns.length - 1 === columnIndex) {
                                                                                return (
                                                                                    <div className="u-mt-1 u-flex u-h-center">
                                                                                        <Icon
                                                                                            icon="fa6-solid:palette"
                                                                                            onClick={() =>
                                                                                                setColumnStyleDialog({
                                                                                                    show: true,
                                                                                                    index: rowIndex,
                                                                                                })
                                                                                            }
                                                                                        />
                                                                                    </div>
                                                                                );
                                                                            }

                                                                            return <></>;
                                                                        }

                                                                        return (
                                                                            <div key={`highlighted-element-col-${columnIndex}-${rowIndex}`}>
                                                                                <div className="u-mr-1">
                                                                                    <DebouncedTextInput
                                                                                        className="c-sidepanel__input"
                                                                                        onChange={(value) => {
                                                                                            rowChangeHandler(columnIndex, rowIndex, value);
                                                                                        }}
                                                                                        value={row}
                                                                                        maxLength={LIST_ITEM_MAX}
                                                                                    />
                                                                                </div>
                                                                                {renderColumnStyleIcon(
                                                                                    highlightedElement.columns,
                                                                                    columnIndex
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}

                                                                    <div className="u-mt-1">
                                                                        <Icon
                                                                            icon="fa6-solid:palette"
                                                                            onClick={() => {
                                                                                setRowStyleDialog({
                                                                                    show: true,
                                                                                    index: columnIndex,
                                                                                });
                                                                            }}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                }}
                                            </Draggable>
                                        );
                                    })}

                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </div>

            <Modal
                isOpen={rowStyleDialog.show}
                header={t('presEditPage.editSidebar.elements.tableModal.tableRow')}
                description={t('presEditPage.editSidebar.elements.tableModal.manageTableRowStyle')}
                icon="solar:pallete-2-bold"
                onConfirm={() => {
                    setRowStyleDialog({...rowStyleDialog, show: false});
                }}
                onCancel={() => {
                    setRowStyleDialog({...rowStyleDialog, show: false});
                }}
                showCancel
                showConfirm
                cancelText={t('common.cancel')}
                confirmText={t('common.confirm')}
            >
                <div className="body-overflow">
                    <SegmentDialog
                        styles={highlightedElement.rowStyles}
                        styleDialog={rowStyleDialog}
                        highlightedElementChangeHandler={(newRowStyle) => updateHighlightedElement.single('rowStyles', newRowStyle)}
                    />
                </div>
            </Modal>

            <Modal
                isOpen={columnStyleDialog.show}
                header={t('presEditPage.editSidebar.elements.tableModal.tableColumn')}
                description={t('presEditPage.editSidebar.elements.tableModal.manageTableColumnStyle')}
                icon="solar:pallete-2-bold"
                onConfirm={() => {
                    setColumnStyleDialog({...columnStyleDialog, show: false});
                }}
                onCancel={() => {
                    setColumnStyleDialog({...columnStyleDialog, show: false});
                }}
                showCancel
                showConfirm
                cancelText={t('common.cancel')}
                confirmText={t('common.confirm')}
            >
                <div className="body-overflow">
                    <SegmentDialog
                        styles={highlightedElement.columnStyles}
                        styleDialog={columnStyleDialog}
                        highlightedElementChangeHandler={(newColumnStyle) =>
                            updateHighlightedElement.single('columnStyles', newColumnStyle)
                        }
                    />
                </div>
            </Modal>
        </>
    );
}

export default HighlightedStaticTableElementComponent;
